import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { UserStateService } from './services/user-state.service';
import { WebSocketService } from './services/web-socket.service';
import { SharedFeaturesService } from './services/shared-features.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  template: `
    <app-loading-screen *ngIf="isLoading"></app-loading-screen>
    <router-outlet *ngIf="!isLoading"></router-outlet>
  `,
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  isLoading = true;

  constructor(
    private userStateService: UserStateService,
    private router: Router,
    private webSocketService: WebSocketService,
    private sharedFeatures: SharedFeaturesService
  ) {}

  // Angular does not await ngOnInit, so an async hook turns any rejection
  // inside it into an unhandled promise rejection (Sonar typescript:S6544).
  // The hook stays void and the awaited work is handled here.
  ngOnInit(): void {
    void this.initialiseApp().catch(err =>
      console.error('AppComponent initialisation failed', err));
  }

  private async initialiseApp() {
    // Wait for user state to be initialized
    await this.userStateService.waitForInitialization();

    this.connectTaskNotifications();

    // Handle root path navigation
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        // Covers SPA login: the socket can't connect until a session exists.
        this.connectTaskNotifications();
        // If user lands on root path and is authenticated, redirect to their default route
        if (event.url === '/' && this.userStateService.isAuthenticated()) {
          this.userStateService.navigateToDefaultRoute();
        }
      });
    
    // Check if we're on root path now
    if (this.router.url === '/' && this.userStateService.isAuthenticated()) {
      this.userStateService.navigateToDefaultRoute();
    }
    
    // Hide loading screen
    this.isLoading = false;
  }

  private notificationsWired = false;

  /** App-wide Celery task status toasts over the shared per-user socket. */
  private connectTaskNotifications(): void {
    if (!this.userStateService.isAuthenticated()) {
      return;
    }
    this.webSocketService.connect(); // idempotent
    if (this.notificationsWired) {
      return;
    }
    this.notificationsWired = true;
    this.webSocketService.getMessages().subscribe((response: any) => {
      if (response?.message?.type !== 'task_status') {
        return; // upload_file_* messages are handled by their own screens
      }
      const { status, detail, solution_name, version_name, task_id } =
        response.message.message || {};
      const ok = status === 'SUCCESS';
      const run = solution_name
        ? version_name
          ? `Run of ${version_name} of "${solution_name}"`
          : `Run of "${solution_name}"`
        : 'Run';
      const ref = task_id ? ` (task #${String(task_id).slice(0, 8)})` : '';
      const text = ok
        ? `${run}${ref} finished successfully.`
        : `${run}${ref} failed${detail ? ': ' + detail : '.'}`;
      this.sharedFeatures.openSnackBar(
        text,
        'Close',
        ok ? 'success-snackbar' : 'error-snackbar'
      );
    });
  }
}
